import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, GraduationCap } from "lucide-react";
import { dueLine, stateOf } from "../../lib/portalKinds";
import { byLesson, isNew, LIBRARY_KIND_LABELS, libraryStatus } from "../../lib/courseLessons";
import { Chip } from "./PortalShell";

/**
 * "Lecțiile cursului" (28.10.2026): what a group received from the course
 * library, lesson by lesson. A participant sees the lessons that have begun;
 * the trainer sees all of them, with whether and when participants see each.
 */
export default function LibraryLessons({ activities, lessons, trainer, groupId }) {
  const { course, lessons: groups } = byLesson(activities, lessons);
  if (!course.length && !groups.length) return null;
  return (
    <section className="mt-4 rounded-xl border border-[#E5E7EB] bg-white p-4" aria-labelledby={`portal-lessons-${groupId}`}
      data-testid="portal-library-lessons">
      <div className="flex items-center gap-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#F0E7F4] text-[#7A4986]" aria-hidden="true">
          <GraduationCap size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 id={`portal-lessons-${groupId}`} className="text-[15px] font-bold text-[#373A45]">Lecțiile cursului</h3>
          <p className="text-xs text-[#6B7280]">
            {trainer
              ? "Din biblioteca cursului: cursanții văd fiecare lecție de la începutul sesiunii ei. Tu le vezi pe toate."
              : "Prezentarea, suportul de curs și tema fiecărei lecții, pe măsură ce ajungeți la ea."}
          </p>
        </div>
      </div>

      {trainer && course.length > 0 && <Rows rows={course} trainer />}
      {groups.map((les) => (
        <div key={les.n} className="mt-4" data-testid={`portal-lesson-${les.n}`}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#7A4986]">Lecția {les.n}</p>
          {les.name && <p className="text-sm font-semibold text-[#373A45]">{les.name}</p>}
          <Rows rows={les.items} trainer={trainer} />
        </div>
      ))}
    </section>
  );
}

function Rows({ rows, trainer }) {
  return (
    <ul className="mt-2 divide-y divide-[#E5E7EB] overflow-hidden rounded-lg border border-[#E5E7EB]">
      {rows.map((a) => {
        const st = trainer ? libraryStatus(a) : a.kind === "assignment" ? stateOf(a) : null;
        return (
          <li key={a.id}>
            <Link to={`/portal/tema/${a.id}`} data-testid="portal-library-row"
              className="group flex items-center gap-3 px-3 py-2.5 hover:bg-[#F9FAFB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#965EA7]">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]">
                  {LIBRARY_KIND_LABELS[a.library_kind] || ""}
                </p>
                <p className="truncate text-sm font-semibold text-[#373A45] group-hover:text-[#965EA7]">{a.title}</p>
                {a.kind === "assignment" && (
                  <p className="text-xs text-[#6B7280]">
                    {dueLine(a.due_at)}
                    {trainer && ` · ${a.submitted_count === 1 ? "o predare" : `${a.submitted_count || 0} predări`}`}
                  </p>
                )}
              </div>
              {!trainer && isNew(a) && <Chip tone="good">Nou</Chip>}
              {st && <Chip tone={st.tone}>{st.label}</Chip>}
              <ChevronRight size={16} className="shrink-0 text-[#9CA3AF]" />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
